import { supabase } from '@/lib/supabase';
import type { Database, PunchItemRow } from '@/types/database';

type PunchInsert = Database['public']['Tables']['punch_items']['Insert'];
type PunchUpdate = Database['public']['Tables']['punch_items']['Update'];

/**
 * Every punch item on a project — including the ones in no room (0048).
 *
 * This read `*, rooms!inner(project_id)` until 0048. `!inner` is an INNER
 * join: a punch item with no room matches no row in `rooms` and would have
 * been dropped from the list silently, which reads as "I added it and it
 * vanished". The column is on the row now, so there is no join at all — one
 * request either way, and one fewer thing to get wrong.
 */
export async function listPunchForProject(projectId: string): Promise<PunchItemRow[]> {
  const { data, error } = await supabase
    .from('punch_items').select('*')
    .eq('project_id', projectId)
    .order('complete', { ascending: true })
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function listPunch(roomId: string): Promise<PunchItemRow[]> {
  const { data, error } = await supabase
    .from('punch_items')
    .select('*')
    .eq('room_id', roomId)
    .order('complete', { ascending: true })
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function createPunch(input: PunchInsert): Promise<PunchItemRow> {
  const { data, error } = await supabase.from('punch_items').insert(input).select().single();
  if (error) throw error;
  return data;
}

export async function updatePunch(id: string, patch: PunchUpdate): Promise<PunchItemRow> {
  const { data, error } = await supabase.from('punch_items').update(patch).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function deletePunch(id: string): Promise<void> {
  const { error } = await supabase.from('punch_items').delete().eq('id', id);
  if (error) throw error;
}
